<template>
  <div class="org-chart-wrapper">
    <div class="controls">
      <v-autocomplete
        v-model="searchSelected"
        :items="autocompleteItems"
        :search-input.sync="searchQuery"
        hide-no-data
        hide-details
        placeholder="Search"
        item-value="id"
        item-text="searchLabel"
        return-object
        dense
        outlined
        clearable
        class="search-field"
        prepend-inner-icon="mdi-magnify"
        :filter="() => true"
        @change="goToUser"
      >
        <template v-slot:item="{ item }">
          <v-list-item-content>
            <v-list-item-title>{{ item.blueTag }}</v-list-item-title>
          </v-list-item-content>
        </template>
        <template v-slot:selection="{ item }">
          <span v-if="item">{{ item.blueTag }}</span>
        </template>
      </v-autocomplete>
      <button  
        v-if="!dragEnabled" 
        @click="swapLayout" 
        class="action-btn"
        :disabled="loading "
      >
        Swap layout
      </button>
      
      <div v-else class="drag-actions">
        <button class="action-btn primary" @click="disableDrag">Save</button>
        <button 
          class="action-btn" 
          :disabled="undoActions.length === 0" 
          @click="undo"
        >
          Undo
        </button>
        <button 
          class="action-btn" 
          :disabled="redoActions.length === 0" 
          @click="redo"
        >
          Redo
        </button>
        <button class="action-btn warning" @click="cancelDrag">Cancel</button>
      </div>
      <v-switch label="My Tree" class="ml-5 pa-0 mt-3" v-model="myTree"/>
      <v-switch label="Assignments" class="ml-3 pa-0 mt-3" v-model="showAssignments" :loading="loadingAssignments" color="green"/>
    </div>

    <div class="chart-and-sidebar">
      <div v-if="loading" class="loading-container">
        <div class="loading-message">Cargando usuarios...</div>
      </div>
      <div ref="chartContainer" class="chart-container" @contextmenu.prevent="onChartContextMenu"></div>

      <div v-if="nodeMenu.show" class="node-menu-backdrop" @click="closeNodeMenu" />
      <div
        v-if="nodeMenu.show"
        class="node-action-menu"
        :style="{ left: nodeMenu.x + 'px', top: nodeMenu.y + 'px' }"
        @click.stop
      >
        <button v-if="canEditUsers" class="node-action-item" @click="nodeMenuGoToPeople" title="Go to People">
          <v-icon small>mdi-account</v-icon>
          <span>People</span>
        </button>
        <button class="node-action-item" @click="nodeMenuEditManager" title="Edit manager">
          <v-icon small>mdi-account-supervisor</v-icon>
          <span>Edit manager</span>
        </button>
      </div>

      <div class="chart-legend">
        <div class="chart-legend-item">
          <svg width="36" height="12">
            <line x1="0" y1="6" x2="36" y2="6" stroke="#43A047" stroke-width="2"/>
          </svg>
          <span>Direct manager</span>
        </div>
        <div class="chart-legend-item">
          <svg width="36" height="12" style="overflow:visible">
            <line x1="0" y1="6" x2="36" y2="6" stroke="#167bd7" stroke-width="2" />
          </svg>
          <span>Assigned manager</span>
        </div>
      </div>

      <div v-if="sidebarOpen" class="node-sidebar">
          <button class="sidebar-close" @click="closeSidebar">
            <v-icon small>mdi-close</v-icon>
          </button>

          <v-menu
            v-model="selectingUser"
            :close-on-content-click="false"
            offset-y
            left
            nudge-bottom="8"
            min-width="240"
          >
            <template #activator="{ on }">
              <div class="sidebar-avatar" v-on="on" title="Cambiar usuario">
                <img
                  :src="selectedNode.profilePicSrc || ''"
                  :alt="selectedNode.fullName"
                  class="sidebar-avatar-img"
                />
                <div class="sidebar-avatar-edit-overlay">
                  <v-icon small color="white">mdi-pencil</v-icon>
                </div>
              </div>
            </template>

            <v-card style="min-width:240px; max-height:320px; display:flex; flex-direction:column; background-color: var(--v-newDesignBackground-base)">
              <div class="px-2 pt-2">
                <v-text-field
                  ref="userSearchField"
                  v-model="userSearch"
                  dense
                  outlined
                  hide-details
                  placeholder="Buscar..."
                  prepend-inner-icon="mdi-magnify"
                  @keydown.esc="selectingUser = false"
                />
              </div>
              <v-list dense class="overflow-y-auto">
                <v-list-item
                  v-for="user in originalUsers"
                  :key="user.id"
                  @click="onSelectUser(user.id)"
                  style="background-color: var(--v-newDesignBackground-base)"
                >
                  <v-list-item-avatar size="32">
                    <v-img :src="user.profilePicSrc" />
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>{{ user.blueTag }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card>
          </v-menu>

          <div class="sidebar-name">{{ selectedNode.fullName }}</div>
          <div class="sidebar-bluetag">@{{ selectedNode.blueTag }}</div>

          <v-divider class="my-3" />
          

          <div class="sidebar-section">
            <div v-if="selectedNode.jobTitle || selectedNode.roles" class="sidebar-row">
              <v-icon small class="sidebar-icon">mdi-briefcase-outline</v-icon>
              <span>{{ selectedNode.jobTitle || selectedNode.roles }}</span>
            </div>
            <div v-if="selectedNodeManager" class="sidebar-row">
              <v-icon small class="sidebar-icon">mdi-account-supervisor-outline</v-icon>
              <span>{{ selectedNodeManager }}</span>
            </div>
          </div>

          <v-divider class="my-3" />
          <v-row dense justify="center">
            <v-col class="col-login" :class="'mb-1'">
              <span>Manager</span>
              <q-user-autocomplete
                :handleEsc="true"
                dense
                :handleSave="true"
                @save="onManagerChange"
                class="user-autocomplete"
                outlined
                v-model="selectedNode.managerId" :items="filteredSidebarUsers" 
                item-value="id" item-text="blueTag" item-subtitle="fullName"
              >
              </q-user-autocomplete>
            </v-col>
          </v-row>
        </div>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import { OrgChart } from 'd3-org-chart';
import logo from '../../../public/logo-surf.png';

export default {
  name: 'OrgChart',
  data() {
    return {
      dragEnabled: false,
      undoActions: [],
      redoActions: [],
      users: [],
      originalUsers: [],
      loading: false,
      canEdit: false,
      searchQuery: '',
      searchSelected: null,
      canEditUsers: false,
      orientation: 'top',
      myTree: false,
      currentUserId: null,
      sidebarOpen: false,
      selectedNode: {},
      selectingUser: false,
      userSearch: '',
      showAssignments: true,
      loadingAssignments: false,
      assignmentNodes: [],
      assignments: [],
      highlightUserId: null,
      nodeMenu: { show: false, x: 0, y: 0, nodeId: null, userId: null },
    };
  },
  watch: {
    searchSelected(val) {
      if (!val && this.chart) {
        this.highlightUserId = null;
        // clearHighlighting() limpia _highlighted y _upToTheRootHighlighted.
        // Los flags custom (_assignmentPathHighlighted, _assignmentParentHighlighted) hay que limpiarlos manualmente.
        const state = this.chart.getChartState();
        (state.data || []).forEach(d => {
          d._assignmentPathHighlighted = false;
          d._assignmentParentHighlighted = false;
        });
        this.chart.clearHighlighting().render();
      }
    },
    selectingUser(val) {
      if (val) {
        this.userSearch = '';
        this.$nextTick(() => {
          this.$refs.userSearchField && this.$refs.userSearchField.focus();
        });
      }
    },
    currentUserId(val) {
      if (val && this.myTree) {
        this.applyMyTreeFilter();
      }
    },
    showAssignments(val) {
      if (val) {
        this.loadAssignments();
      } else {
        this.assignmentNodes = [];
        if (this.chart) this.chart.data(this.users).render().expandAll();
      }
    },
    myTree(val) {
      if (val) {
        this.applyMyTreeFilter();
      } else {
        this.users = this.normalizeUsersForOrgChart([...this.originalUsers]);
        const chartData = this.chartData;
        this.chart.data(chartData).render().expandAll().fit();
      }
    },
  },
  computed: {
    visibleAssignmentNodes() {
      if (!this.showAssignments || !this.assignmentNodes.length) return [];
      const parentIds = new Set(this.users.map(u => u.id).filter(Boolean));
      const withValidParent = this.assignmentNodes.filter(a =>
        a.managerId && parentIds.has(a.managerId)
      );
      if (!this.myTree) return withValidParent;
      const visibleUserIds = parentIds;
      return withValidParent.filter(a => a.userId && visibleUserIds.has(a.userId));
    },
    dualUserIdSet() {
      if (!this.assignmentNodes.length) return new Set();
      const allUserManagerMap = new Map(this.originalUsers.map(u => [u.id, u.managerId]));
      const dualIds = new Set();
      this.assignmentNodes.forEach(a => {
        const orgManagerId = allUserManagerMap.get(a.userId);
        if (orgManagerId && orgManagerId !== 'virtual-root-org' && a.managerId === orgManagerId) {
          dualIds.add(a.userId);
        }
      });
      return dualIds;
    },
    chartData() {
      if (!this.showAssignments || !this.visibleAssignmentNodes.length) return this.users;
      let assignments = this.visibleAssignmentNodes;
      // 3. Colapsar: si varios assignments tienen el mismo delivery manager, mostrar solo 1 con el total en el badge
      const byManager = new Map();
      assignments.forEach(a => {
        const key = `${a.managerId || 'none'}__${a.userId || 'none'}`;
        if (!byManager.has(key)) byManager.set(key, []);
        byManager.get(key).push(a);
      });
      assignments = [];
      byManager.forEach((group) => {
        if (group.length === 1) {
          assignments.push(group[0]);
        } else {
          const first = { ...group[0], _collapsedCount: group.length, _collapsedAssignments: group };
          assignments.push(first);
        }
      });
      // --- Dual relationship: usuario cuyo managerId (org) === deliveryManagerId (assignment) ---
      // Para esos usuarios: eliminar el nodo de usuario y promover el assignment node como padre.
      const userManagerMap = new Map(this.users.map(u => [u.id, u.managerId]));
      const dualUserIds = new Set();
      const dualNodeIdByUserId = new Map(); // userId → "dual-${userId}"
      assignments.forEach(a => {
        const userOrgManagerId = userManagerMap.get(a.userId);
        if (userOrgManagerId && userOrgManagerId !== 'virtual-root-org' && a.managerId === userOrgManagerId) {
          dualUserIds.add(a.userId);
          dualNodeIdByUserId.set(a.userId, `dual-${a.userId}`);
        }
      });

      // Dar al assignment dual un ID estable y marcarlo
      assignments = assignments.map(a => {
        if (dualUserIds.has(a.userId) && a.managerId === userManagerMap.get(a.userId)) {
          return { ...a, id: `dual-${a.userId}`, _isDualNode: true };
        }
        return a;
      });

      // userId+managerId de cada assignment visible (post-colapso, antes de remap)
      const assignmentKeys = new Set(
        assignments.map(a => `${a.userId}__${a.managerId}`)
      );

      // Función de remap: si managerId apuntaba a un dual user, ahora apunta al dual assignment node
      const remapManagerId = (mid) =>
        (mid && dualNodeIdByUserId.has(mid)) ? dualNodeIdByUserId.get(mid) : mid;

      // IDs de usuarios que son managers de al menos otro nodo (user o assignment)
      const parentOfOthers = new Set();
      this.users.forEach(u => { if (u.managerId) parentOfOthers.add(u.managerId); });
      assignments.forEach(a => { if (a.managerId) parentOfOthers.add(a.managerId); });
      // Usuarios que tienen al menos un assignment visible
      const usersWithAssignment = new Set(assignments.map(a => a.userId));

      // Usuarios sin manager org (virtual-root) que tienen assignments: sus children cuelgan del primer assignment
      const usersWithNullManagerAndAssignments = new Set(
        this.users
          .filter(u => u.id !== 'virtual-root-org' && (u.managerId === 'virtual-root-org' || !u.managerId) && usersWithAssignment.has(u.id))
          .map(u => u.id)
      );
      const anchorAssignmentIdByUserId = new Map();
      usersWithNullManagerAndAssignments.forEach(userId => {
        const firstAssignment = assignments.find(a => a.userId === userId);
        if (firstAssignment) anchorAssignmentIdByUserId.set(userId, firstAssignment.id);
      });
      const finalRemapManagerId = (mid) => {
        if (mid && anchorAssignmentIdByUserId.has(mid)) return anchorAssignmentIdByUserId.get(mid);
        return remapManagerId(mid);
      };

      // Remap de managerId: dual user → dual node; usuario sin manager con assignments → su primer assignment (anchor)
      assignments = assignments.map(a => ({ ...a, managerId: finalRemapManagerId(a.managerId) }));

      // Excluir nodos user:
      // - Siempre: dual users (el assignment node los reemplaza)
      // - Siempre: usuarios con managerId null que ya aparecen como assignment (sus children van al anchor)
      // - Condicional: hoja con assignment bajo mismo manager
      const filteredUsers = this.users
        .filter(u => {
          if (dualUserIds.has(u.id)) return false;
          if ((!u.managerId || u.managerId === 'virtual-root-org') && usersWithAssignment.has(u.id)) return false;
          if (!parentOfOthers.has(u.id)) {
            if (assignmentKeys.has(`${u.id}__${u.managerId}`)) return false;
          }
          return true;
        })
        .map(u => ({ ...u, managerId: finalRemapManagerId(u.managerId) }));
      const highlightUid = this.highlightUserId;
      const assignmentsWithHighlight = highlightUid
        ? assignments.map(a => {
            const isAssignee = a.userId === highlightUid ||
              (a._collapsedAssignments && a._collapsedAssignments.some(x => x.userId === highlightUid));
            return isAssignee ? { ...a, _highlighted: true } : a;
          })
        : assignments;
      const allNodes = [...filteredUsers, ...assignmentsWithHighlight];
      if (highlightUid) {
        const pathFromAssignmentsToRoot = new Set();
        const directParentsOfHighlightedAssignments = new Set();
        const nodeById = new Map(allNodes.map(n => [n.id, n]));
        assignmentsWithHighlight.forEach(a => {
          if (!a._highlighted) return;
          directParentsOfHighlightedAssignments.add(a.managerId);
          let mid = a.managerId;
          while (mid) {
            pathFromAssignmentsToRoot.add(mid);
            const node = nodeById.get(mid);
            mid = node ? node.managerId : null;
            if (!mid || mid === 'virtual-root-org') break;
          }
        });
        if (pathFromAssignmentsToRoot.size > 0) pathFromAssignmentsToRoot.add('virtual-root-org');
        return allNodes.map(n => {
          let out = n;
          if (pathFromAssignmentsToRoot.has(n.id)) out = { ...out, _assignmentPathHighlighted: true };
          if (directParentsOfHighlightedAssignments.has(n.id)) out = { ...out, _assignmentParentHighlighted: true };
          return out;
        });
      }
      return allNodes;
    },
    searchableUsers() {
      const regularUsers = this.users.filter(u => u.id !== 'virtual-root-org');
      const dualUsers = this.originalUsers.filter(u => this.dualUserIdSet.has(u.id));
      return [...regularUsers, ...dualUsers];
    },
    searchResults() {
      if (!this.searchQuery || this.searchQuery.length < 2) {
        return [];
      }
      const q = this.searchQuery.toLowerCase().trim();
      return this.searchableUsers
        .filter(u => {
          const blueTag = (u.blueTag || '').toLowerCase();
          const fullName = (u.fullName || '').toLowerCase();
          const email = (u.email || '').toLowerCase();
          return blueTag.includes(q) || fullName.includes(q) || email.includes(q);
        })
        .map(u => ({ ...u, searchLabel: `${u.blueTag || ''} ${u.fullName || ''}`.trim() }))
        .slice(0, 15);
    },
    autocompleteItems() {
      const withLabel = (list) =>
        list.map(u => ({ ...u, searchLabel: `${u.blueTag || ''} ${u.fullName || ''}`.trim() }));
      if (this.searchQuery && this.searchQuery.length >= 2) {
        return this.searchResults;
      }
      return withLabel(this.searchableUsers);
    },
    filteredSidebarUsers() {
      const q = (this.userSearch || '').toLowerCase().trim();
      const virtualRoot = this.users.find(u => u.id === 'virtual-root-org');
      const regular = this.originalUsers;
      const all = virtualRoot ? [virtualRoot, ...regular] : regular;
      if (!q) return all;
      return all.filter(u =>
        (u.blueTag || '').toLowerCase().includes(q) ||
        (u.fullName || '').toLowerCase().includes(q)
      );
    },
    selectedNodeManager() {
      if (!this.selectedNode) return null;
      const managerId = this.selectedNode.managerId;
      if (!managerId) return null;
      const manager = this.users.find(u => u.id === managerId);
      return manager ? (manager.fullName || manager.blueTag) : null;
    },
  },
  mounted() {
    document.title = 'Organization Chart'
    this.chart = null;
    this.dragNode = null;
    this.dropNode = null;
    this.dragStartX = 0;
    this.dragStartY = 0;
    this.isDragStarting = false;
    this.$blueSystem.getCurrentUser().then(user => {
      this.currentUserId = user.id;
    });
    this.loadUsers();
    this.loadAssignments();
    this.$security.hasRequiredPermission('add').then(canEdit => {
      this.canEdit = canEdit;
    });
    this.$security.hasRequiredPermission('detail', 14).then(canEditUsers => {
      this.canEditUsers = canEditUsers;
    })
  },
  beforeDestroy() {
    this.chart = null;
  },
  methods: {
    test() {
      this.chart.render().fit();
    },
    swapLayout() {
      this.chart.layout(this.orientation === 'top' ? 'left' : 'top').setCentered('virtual-root-org').render().expandAll().fit();
      this.orientation = this.orientation === 'top' ? 'left' : 'top';
    },
    loadUsers() {
      this.loading = true;
      this.$http
        .get('/api/user?forOrganizationChart=true&usersOnly=true&active=true&onlyInterns=true')
        .then(response => {
          let allUsers = response.data.map(user => ({
            ...user,
            managerId: user.managerId || user.parentId || null
          }));

          allUsers.forEach(this.$q.profilePicSrc, true);

          this.originalUsers = [...allUsers];

          // Si los assignments ya cargaron antes que los usuarios, completar jobTitle ahora
          if (this.assignmentNodes.length) {
            this.assignmentNodes = this.assignmentNodes.map(n => {
              const user = allUsers.find(u => u.id === n.userId);
              return user ? { ...n, jobTitle: user.jobTitle || user.roles || n.jobTitle } : n;
            });
          }

          if (this.myTree) {
            const currentUser = allUsers.find(u => u.id === this.currentUserId);
            const subtree = currentUser
              ? this.getSubtreeWithAssignments(this.currentUserId, allUsers, this.assignmentNodes)
              : [];
            this.users = currentUser
              ? [{ ...currentUser, managerId: null }, ...subtree]
              : this.normalizeUsersForOrgChart([...allUsers]);
          } else {
            this.users = this.normalizeUsersForOrgChart([...allUsers]);
          }

          this.initChart();
          this.loading = false;
        })
        .catch(response => {
          window.getApp.error("No se pudieron cargar los usuarios.");
          console.error(response);
          this.loading = false;
        });
    },
    
    normalizeUsersForOrgChart(users) {
      // Siempre usar virtual root "Blue People": todos los usuarios sin ManagerId son hijos de la organización
      return this.createVirtualRoot(users);
    },

    getCountHoverText(node) {
      if (!node) return '';
      const userId = node.type === 'assignment' ? node.userId : node.id;
      if (!userId || userId === 'virtual-root-org') return '';

      const lines = [];

      // Siempre mostrar TODOS los assignments del usuario en el tooltip,
      // sin importar desde qué nodo se hace hover (collapsed, individual o user node).
      const assignments = this.visibleAssignmentNodes.filter(a => a.userId === userId);

      if (assignments.length > 0) {
        lines.push('Managers');
        assignments.forEach(a => {
          const proj = a.projectName || '';
          const dm = a.deliveryManagerBlueTag || '';
          lines.push(`- ${proj}: ${dm}`);
        });
      }

      // Direct Manager (org)
      const userData = this.originalUsers.find(u => u.id === userId);
      if (userData && userData.managerId && userData.managerId !== 'virtual-root-org') {
        const manager = this.originalUsers.find(u => u.id === userData.managerId);
        const managerBlueTag = manager ? (manager.blueTag || manager.fullName || '') : '';
        if (managerBlueTag) lines.push(`Direct Manager: ${managerBlueTag}`);
      }

      return lines.join('\n');
    },

    getAppearanceCount(node) {
      if (!node) return 0;
      const userId = node.type === 'assignment' ? node.userId : node.id;
      if (!userId || userId === 'virtual-root-org') return 0;

      let count = 0;
      // Como nodo user (0 o 1)
      // Como nodo assignment (todas las veces)
      count += this.visibleAssignmentNodes.filter(a => a.userId === userId).length;
      // Como manager de otros (si aparece 2+ veces, solo 1)
      // if (this.users.some(u => u.managerId === userId)) count += 1;
      // // Como delivery manager (si aparece 2+ veces, solo 1)
      // if (this.assignmentNodes.some(a => a.managerId === userId)) count += 1;

      return count;
    },

    createVirtualRoot(users) {
      const virtualRoot = {
        id: 'virtual-root-org',
        fullName: 'Blue People',
        roles: 'Organización',
        managerId: null,
        active: true,
        type: 0,
        email: '',
        blueTag: 'Blue People',
        jobTitle: '',
        badges: [],
        customerProjects: [],
        department: 0,
        experienceLevelName: '',
        profilePicSrc: logo
      };

      const normalizedUsers = users.map(user => {
        if (!user.managerId) {
          return { ...user, managerId: virtualRoot.id };
        }
        return user;
      });

      return [virtualRoot, ...normalizedUsers];
    },
    initChart() {
      if (!this.$refs.chartContainer || !this.users || this.users.length === 0) {
        return;
      }

      const vm = this;

      this.chart = new OrgChart()
          .defs((d) => {
            return `
              <marker id="arrow-${d.from + "_" + d.to}" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
                <path d="M 0 0 L 10 3.5 L 0 7" fill="#E27396" />
              </marker>
            `;
          })
          .nodeHeight((d) => 100)
          .nodeWidth((d) => 180)
          .childrenMargin((d) => 50)
          .compactMarginBetween((d) => 35)
          .compactMarginPair((d) => 30)
          .neighbourMargin((a, b) => 20)
          .container(this.$refs.chartContainer)
          .parentNodeId((d) => {
            if (d.data) {
              return d.data.managerId || null;
            }
            return d.managerId || null;
          })
          .data(this.chartData)
          .initialExpandLevel(Infinity)
          .nodeContent((d) => this.generateContent(d))
          .nodeEnter(function (node) {
            d3.select(this).call(
              d3.drag()
                .filter(function () {
                  return vm.dragEnabled && this.classList.contains('draggable');
                })
                .on('start', function (event, d) {
                  vm.onDragStart(this, event, d);
                })
                .on('drag', function (event, d) {
                  vm.onDrag(this, event);
                })
                .on('end', function (event, d) {
                  vm.onDragEnd(this, event);
                })
            );
          })
          .nodeUpdate(function (d) {
            const el = d3.select(this);
            const nodeId = d.data?.id ?? d.id;
            const isAssignment = d.data?.type === 'assignment';

            if (nodeId === 'virtual-root-org') {
              el.classed('droppable', true);
              el.classed('draggable', false);
              el.style('cursor', 'default');
              el.on('click', null);
            } else if (isAssignment) {
              el.classed('droppable', false);
              el.classed('draggable', false);
              el.style('cursor', 'pointer');
            } else {
              el.classed('droppable', true);
              el.classed('draggable', true);
              el.style('cursor', vm.dragEnabled ? '' : 'default');
              el.on('click', null);
            }

            // Actualizar borde del nodo según estado de highlight (corre en todos los nodos en cada render)
            const isGreenBorder = d.data?._assignmentPathHighlighted || d.data?._assignmentParentHighlighted || (d.data?._highlighted && isAssignment);
            const isPinkBorder  = d.data?._upToTheRootHighlighted && !isGreenBorder;
            const container = el.select('.content-container');
            if (isGreenBorder) {
              container.style('border', '4px solid #E27396');
            } else if (isPinkBorder) {
              container.style('border', '4px solid #E27396');
            } else {
              container.style('border', '1.5px solid #167bd7');
            }
          })
          .linkUpdate(function (d) {
            const el = d3.select(this);
            const pathHighlighted = d.data?._upToTheRootHighlighted;
            const assignmentHighlighted = d.data?._highlighted && d.data?.type === 'assignment';
            const assignmentPathHighlighted = d.data?._assignmentPathHighlighted;
            const parentOfAssignmentHighlighted = d.data?._assignmentParentHighlighted;
            const highlighted = pathHighlighted || assignmentHighlighted || parentOfAssignmentHighlighted || assignmentPathHighlighted;
            const isAssignment = d.data?.type === 'assignment';
            const isGreenTarget = isAssignment || assignmentPathHighlighted || (parentOfAssignmentHighlighted && highlighted);

            const isUserToUser =
              !isAssignment &&
              d.data?.id !== 'virtual-root-org' &&
              d.parent?.data?.type !== 'assignment';

            const defaultStroke = isUserToUser ? '#43A047' : '#167bd7';
            const strokeColor = highlighted ? (isGreenTarget ? '#E27396' : '#E27396') : defaultStroke;
            const strokeOpacity = highlighted ? 1 : (isAssignment ? 0.8 : 0.6);
            el.classed('link-highlighted', highlighted)
              .attr('stroke', strokeColor)
              .attr('stroke-width', highlighted ? 4 : 2)
              .attr('stroke-opacity', strokeOpacity)
              .attr('stroke-dasharray', null);
            if (highlighted) el.raise();

            if (d.data?._isDualNode) {
              d3.select(this)
                .attr('stroke-width', 2)
                .attr('stroke-dasharray', null)
                .style('filter', 'drop-shadow(6px -5px 0px red)');
            } else {
              d3.select(this).style('filter', null);
            }
          })
          .render()
          .fit();

      if (this._nodeMenuHandler) {
        this.$refs.chartContainer.removeEventListener('click', this._nodeMenuHandler);
      }
      this._nodeMenuHandler = (e) => {
        const btn = e.target.closest('.node-menu-btn');
        if (!btn || this.dragEnabled) return;
        e.stopPropagation();
        const rect = btn.getBoundingClientRect();
        this.nodeMenu = {
          show: true,
          x: rect.left,
          y: rect.bottom + 4,
          nodeId: btn.dataset.nodeId,
          userId: btn.dataset.userId,
        };
      };
      this.$refs.chartContainer.addEventListener('click', this._nodeMenuHandler);
    },


    onDragStart(element, event, node) {
      this.dragNode = node;
      const width = node.width || 220;
      const half = width / 2;
      
      this.dragStartX = event.x - half;
      this.dragStartY = event.y;
      this.isDragStarting = true;

      d3.select(element).classed('dragging', true);
    },

    onDrag(element, event) {
      if (!this.dragNode) return;

      const state = this.chart.getChartState();
      const g = d3.select(element);

      if (this.isDragStarting) {
        this.isDragStarting = false;
        this.$refs.chartContainer.classList.add('dragging-active');

        g.raise();

        const descendants = this.dragNode.descendants();
        const linksToRemove = [...(descendants || []), this.dragNode];
        const nodesToRemove = descendants.filter(
          (x) => x.data.id !== this.dragNode.data.id
        );

        state.linksWrapper
          .selectAll('path.link')
          .data(linksToRemove, (d) => state.nodeId(d))
          .remove();

        if (nodesToRemove) {
          state.nodesWrapper
            .selectAll('g.node')
            .data(nodesToRemove, (d) => state.nodeId(d))
            .remove();
        }
      }

      this.dropNode = null;
      
      const cP = {
        midX: event.x + this.dragNode.width / 2,
        midY: event.y + this.dragNode.height / 2,
      };

      const allNodes = d3.selectAll('g.node:not(.dragging)');
      allNodes.select('rect').attr('fill', 'none');
      allNodes.classed('drop-target', false);

      const vm = this;

      allNodes
        .filter(function (d2) {
          const cPInner = {
            left: d2.x,
            right: d2.x + d2.width,
            top: d2.y,
            bottom: d2.y + d2.height,
          };

          if (
            cP.midX > cPInner.left &&
            cP.midX < cPInner.right &&
            cP.midY > cPInner.top &&
            cP.midY < cPInner.bottom &&
            this.classList.contains('droppable')
          ) {
            vm.dropNode = d2;
            return d2;
          }
        })
        .each(function () {
          d3.select(this).classed('drop-target', true);
        })
        .select('rect')
        .attr('fill', '#E0EDFE');

      this.dragStartX += event.dx;
      this.dragStartY += event.dy;
      g.attr('transform', `translate(${this.dragStartX},${this.dragStartY})`);
    },

    onDragEnd(element, event) {
      this.$refs.chartContainer.classList.remove('dragging-active');
      d3.selectAll('g.node').classed('drop-target', false);

      if (!this.dragNode) return;

      d3.select(element).classed('dragging', false);

      if (!this.dropNode || (this.dragNode.parent && this.dragNode.parent.id === this.dropNode.id)) {
        this.chart.render().expandAll().fit();
        this.dragNode = null;
        this.dropNode = null;
        return;
      }

      d3.select(element).remove();

      const data = this.chart.getChartState().data;
      const node = data.find((x) => x.id === this.dragNode.data.id);
      const oldManagerIdFromNode = node.managerId || node.data?.managerId;
      const oldManagerId = oldManagerIdFromNode === 'virtual-root-org' ? null : oldManagerIdFromNode;
      
      const dropNodeData = data.find((x) => x.id === this.dropNode.id);
      const isDroppingOnRoot = this.dropNode.id === 'virtual-root-org' || 
                               (!dropNodeData?.managerId && !dropNodeData?.data?.managerId);
      
      const newManagerIdForChart = this.dropNode.id;
      
      node.managerId = newManagerIdForChart;
      if (node.data) {
        node.data.managerId = newManagerIdForChart;
      }
      
      const normalizedUser = this.users.find((u) => u.id === this.dragNode.data.id);
      if (normalizedUser) {
        normalizedUser.managerId = newManagerIdForChart;
      }
      
      const originalUser = this.originalUsers.find((u) => u.id === this.dragNode.data.id);
      if (originalUser) {
        originalUser.managerId = newManagerIdForChart;
      }

      this.redoActions = [];
      this.undoActions.push({
        id: this.dragNode.data.id,
        managerId: oldManagerId,
      });

      this.dropNode = null;
      this.dragNode = null;
      
      this.chart.data(this.chartData).render().expandAll();
      if(isDroppingOnRoot && originalUser.recessApprovers.length == 1) {
        window.getApp.error('The user has only one recess approver and cannot be dropped on the root');
        this.undo();
      }
    },

    enableDrag() {
      this.dragEnabled = true;
      this.$refs.chartContainer.classList.add('drag-enabled');
      this.chart.render();
    },

    async disableDrag() {
      if (this.undoActions.length > 0) {
        try {
          const changes = this.undoActions.map(action => {
            const user = this.users.find(u => u.id === action.id);
            let managerId = user ? (user.managerId || null) : null;
            if (managerId === 'virtual-root-org') {
              managerId = null;
            }
            return {
              id: action.id,
              managerId: managerId
            };
          });

          const allUsersAfterChanges = this.originalUsers.map(originalUser => {
            const change = changes.find(c => c.id === originalUser.id);
            if (change) {
              return { ...originalUser, managerId: change.managerId };
            }
            return originalUser;
          });
          
          await this.$http.post('api/user/updateManagerIds', {
            changes: changes
          });

          await this.loadUsers();

          window.getApp.success('Cambios guardados correctamente');
        } catch (error) {
          console.error('Error al guardar cambios:', error);
          // El interceptor de axios ya muestra el error automáticamente
          // No limpiar los arrays si hay error, para que el usuario pueda intentar de nuevo
          return;
        }
      }

      this.dragEnabled = false;
      this.$refs.chartContainer.classList.remove('drag-enabled');
      this.undoActions = [];
      this.redoActions = [];
      this.chart
    },

    cancelDrag() {
      if (this.undoActions.length > 0) {
        const data = this.chart.getChartState().data;
        [...this.undoActions].reverse().forEach((action) => {
          const node = data.find((x) => x.id === action.id);
          if (node) {
            const managerId = action.managerId === null ? 'virtual-root-org' : action.managerId;
            node.managerId = managerId;
            if (node.data) {
              node.data.managerId = managerId;
            }
          }
          const normalizedUser = this.users.find((u) => u.id === action.id);
          if (normalizedUser) {
            normalizedUser.managerId = action.managerId === null ? 'virtual-root-org' : action.managerId;
          }
          const originalUser = this.originalUsers.find((u) => u.id === action.id);
          if (originalUser) {
            originalUser.managerId = action.managerId;
          }
        });
        this.chart.data(this.chartData).render();
      }
      this.disableDrag();
    },

    undo() {
      const action = this.undoActions.pop();
      if (action) {
        const node = this.chart.getChartState().data.find((x) => x.id === action.id);
        const currentManagerId = node?.managerId || node?.data?.managerId;
        const currentManagerIdForRedo = currentManagerId === 'virtual-root-org' ? null : currentManagerId;
        
        this.redoActions.push({ id: action.id, managerId: currentManagerIdForRedo });
        
        const managerIdToSet = action.managerId === null ? 'virtual-root-org' : action.managerId;
        
        if (node) {
          node.managerId = managerIdToSet;
          if (node.data) {
            node.data.managerId = managerIdToSet;
          }
        }
        
        const normalizedUser = this.users.find((u) => u.id === action.id);
        if (normalizedUser) {
          normalizedUser.managerId = managerIdToSet;
        }
        
        const originalUser = this.originalUsers.find((u) => u.id === action.id);
        if (originalUser) {
          originalUser.managerId = action.managerId;
        }
        
        this.chart.data(this.chartData).render();
      }
    },

    redo() {
      const action = this.redoActions.pop();
      if (action) {
        const node = this.chart.getChartState().data.find((x) => x.id === action.id);
        const currentManagerId = node?.managerId || node?.data?.managerId;
        const currentManagerIdForUndo = currentManagerId === 'virtual-root-org' ? null : currentManagerId;

        this.undoActions.push({ id: action.id, managerId: currentManagerIdForUndo });
        
        const managerIdToSet = action.managerId === null ? 'virtual-root-org' : action.managerId;
        
        if (node) {
          node.managerId = managerIdToSet;
          if (node.data) {
            node.data.managerId = managerIdToSet;
          }
        }
        
        const normalizedUser = this.users.find((u) => u.id === action.id);
        if (normalizedUser) {
          normalizedUser.managerId = managerIdToSet;
        }
        
        const originalUser = this.originalUsers.find((u) => u.id === action.id);
        if (originalUser) {
          originalUser.managerId = action.managerId;
        }
        
        this.chart.data(this.chartData).render();
      }
    },

    goToUser(user) {
      if (!user || !this.chart) return;
      if (user.id === 'virtual-root-org') return;
      this.highlightUserId = user.id;
      const chartNodeId = this.dualUserIdSet.has(user.id) ? `dual-${user.id}` : user.id;

      // Mutamos los flags de highlight directamente en state.data (sin reemplazar el array)
      // para preservar _expanded en cada nodo. Si reemplazáramos con data(chartData),
      // d3-org-chart ocultaría todos los nodos sin _expanded al re-renderizar.
      const state = this.chart.getChartState();
      const highlightById = new Map(this.chartData.map(d => [d.id, d]));
      (state.data || []).forEach(d => {
        const h = highlightById.get(d.id);
        d._highlighted = h?._highlighted || false;
        d._assignmentPathHighlighted = h?._assignmentPathHighlighted || false;
        d._assignmentParentHighlighted = h?._assignmentParentHighlighted || false;
      });

      this.chart
        .setUpToTheRootHighlighted(chartNodeId)
        .setCentered(chartNodeId)
        .render();
    },

    getSubtree(rootId, allUsers) {
      const result = [];
      const queue = [rootId];
      const visited = new Set();
      while (queue.length > 0) {
        const currentId = queue.shift();
        if (visited.has(currentId)) continue;
        visited.add(currentId);
        const children = allUsers.filter(u => u.managerId === currentId);
        children.forEach(child => {
          result.push(child);
          queue.push(child.id);
        });
      }
      return result;
    },

    /**
     * Árbol completo desde rootId usando org (managerId) y assignments (delivery manager).
     * Devuelve lista de usuarios con managerId asignado al padre por el que se descubrieron (BFS).
     */
    getSubtreeWithAssignments(rootId, allUsers, assignmentNodes) {
      const assignmentNodesList = assignmentNodes || [];
      const userById = new Map(allUsers.map(u => [u.id, u]));
      const parentIdByUserId = new Map(); // primer padre que descubre al usuario
      const queue = [rootId];
      const visited = new Set();

      while (queue.length > 0) {
        const currentId = queue.shift();
        if (visited.has(currentId)) continue;
        visited.add(currentId);

        const orgChildren = allUsers.filter(u => u.managerId === currentId);
        const assignmentChildIds = [...new Set(
          assignmentNodesList.filter(a => a.managerId === currentId && a.userId).map(a => a.userId)
        )];

        orgChildren.forEach(child => {
          if (!parentIdByUserId.has(child.id)) {
            parentIdByUserId.set(child.id, currentId);
            queue.push(child.id);
          }
        });
        assignmentChildIds.forEach(childId => {
          if (!parentIdByUserId.has(childId) && userById.has(childId)) {
            parentIdByUserId.set(childId, currentId);
            queue.push(childId);
          }
        });
      }

      return Array.from(parentIdByUserId.entries()).map(([userId, parentId]) => {
        const user = userById.get(userId);
        return user ? { ...user, managerId: parentId } : null;
      }).filter(Boolean);
    },

    applyMyTreeFilter() {
      if (!this.currentUserId) return;
      const currentUser = this.originalUsers.find(u => u.id === this.currentUserId);
      if (!currentUser) return;

      const treeUsers = [
        { ...currentUser, managerId: null },
        ...this.getSubtreeWithAssignments(
          this.currentUserId,
          this.originalUsers,
          this.assignmentNodes
        ),
      ];
      this.users = treeUsers;
      this.chart.data(this.chartData).render().expandAll().fit();
    },

    closeNodeMenu() {
      this.nodeMenu = { show: false, x: 0, y: 0, nodeId: null, userId: null };
    },

    nodeMenuGoToPeople() {
      const userId = this.nodeMenu.userId;
      this.closeNodeMenu();
      if (!userId || userId === 'virtual-root-org') return;
      this.$router.push({ name: 'UserEdit', params: { id: userId } });
    },

    nodeMenuEditManager() {
      const userId = this.nodeMenu.userId;
      this.closeNodeMenu();
      if (!userId || userId === 'virtual-root-org') return;
      this.showNodeSidebar(userId);
    },

    onChartContextMenu(event) {
      if (this.dragEnabled) return;
      const path = event.composedPath ? event.composedPath() : [];
      for (const el of path) {
        if (el === this.$refs.chartContainer) break;
        if (el.classList && el.classList.contains('node')) {
          const datum = d3.select(el).datum();
          if (!datum) break;
          const nodeId = datum.data?.id ?? datum.id;
          if (nodeId && nodeId !== 'virtual-root-org') {
            this.showNodeSidebar(nodeId);
          }
          break;
        }
      }
    },

    showNodeSidebar(nodeId) {
      const user = this.users.find(u => u.id === nodeId);
      if (!user) return;
      this.selectedNode = { ...user };
      this.sidebarOpen = true;
    },

    closeSidebar() {
      this.sidebarOpen = false;
      this.selectedNode = {};
      this.selectingUser = false;
      this.userSearch = '';
    },

    onSelectUser(userId) {
      this.selectingUser = false;
      this.userSearch = '';
      if (!userId) return;
      if (userId === 'virtual-root-org') {
        this.selectedNode = { ...this.selectedNode, managerId: null };
        return;
      }
      const user = this.originalUsers.find(u => u.id === userId);
      if (user) {
        this.$q.profilePicSrc(user, true);
        this.selectedNode = { ...user };
      }
    },

    goToUserEdit(userId) {
      this.$router.push({ name: 'UserEdit', params: { id: userId } });
    },
    profilePicSrc(userId, avatar = false) {
        const profilePicFailover = 'https://cdn.vuetifyjs.com/images/parallax/material2.jpg'
        let src = `api/user/${userId}/profilePicture?size=128`;
        //detect fail for use profilepicfailover
        
        if (!this.$http.defaults.baseURL.endsWith("/")) src = "/" + src;
        return this.$http.defaults.baseURL + src;
    },
    loadAssignments() {
      this.loadingAssignments = true;
      const today = new Date().toISOString().split('T')[0];
      this.$http.post('api/assignment/organization-chart', {
        startDate: today,
        endDate: today,
      })
        .then(response => {
          const raw = response.data.assignments;

          // Convertir cada assignment a nodo compatible con el chart
          // jobTitle se toma directamente del usuario en originalUsers (si ya está cargado)
          this.assignmentNodes = raw.map(a => {
            const user = this.originalUsers.find(u => u.id === a.userId);
            return {
              id: a.id,
              type: 'assignment',
              userId: a.userId,
              blueTag: a.userBlueTag,
              fullName: a.userFullName,
              jobTitle: user ? (user.jobTitle || user.roles || '') : (a.jobTitle || ''),
              projectName: a.name,
              profilePicSrc: this.profilePicSrc(a.userId, true),
              managerId: a.deliveryManagerId || 'virtual-root-org',
              deliveryManagerBlueTag: a.deliveryManagerBlueTag,
              active: true,
            };
          });

          if (this.chart) {
            if (this.myTree) {
              this.applyMyTreeFilter();
            } else {
              this.chart.data(this.chartData).render().expandAll();
            }
          }
        })
        .catch((err) => {
          console.error(err);
          window.getApp.error('Error al cargar assignments');
          this.showAssignments = false;
        })
        .finally(() => {
          this.loadingAssignments = false;
        });
    },

    onManagerChange(newManagerId) {
      const userId = this.selectedNode.id;
      if (!userId) return;

      // 'virtual-root-org' significa sin manager real → null para el servidor
      const normalizeId = id => (!id || id === 'virtual-root-org') ? null : id;

      const previousManagerId = normalizeId(this.selectedNode.managerId);
      const resolvedNewManagerId = normalizeId(newManagerId);

      // Sin cambio real, no hacer nada
      if (resolvedNewManagerId === previousManagerId) return;

      let url = `api/user/updateManager?id=${userId}`;
      if (resolvedNewManagerId) url += `&managerId=${resolvedNewManagerId}`;

      this.$http.post(url)
        .then(() => {
          // En el chart, null se representa como 'virtual-root-org'
          const chartManagerId = resolvedNewManagerId || 'virtual-root-org';

          this.selectedNode = { ...this.selectedNode, managerId: chartManagerId };

          const userInList = this.users.find(u => u.id === userId);
          if (userInList) userInList.managerId = chartManagerId;

          // originalUsers guarda el valor real (null, no 'virtual-root-org')
          const userInOriginal = this.originalUsers.find(u => u.id === userId);
          if (userInOriginal) userInOriginal.managerId = resolvedNewManagerId;

          this.chart.data(this.chartData).render().expandAll();
          window.getApp.success('Manager actualizado correctamente');
        })
        .catch(() => {
          // Revertir: restaurar el valor normalizado del chart
          const revertId = previousManagerId || 'virtual-root-org';
          this.selectedNode = { ...this.selectedNode, managerId: revertId };
        });
    },

    generateContent(d) {
      if (d.data.type === 'assignment') {
        return this.generateAssignmentContent(d);
      }
      const title = d.data.jobTitle || d.data.roles || '';
      const count = this.getAppearanceCount(d.data);
      const hoverText = (this.getCountHoverText(d.data) || '').replace(/"/g, '&quot;').replace(/\n/g, '&#10;');
      const countBadge = count > 1 ? `<span class="node-appearance-count" data-tooltip="${hoverText}" style="position:absolute;top:8px;right:30px;min-width:18px;height:18px;padding:0 5px;border-radius:9px;background:#E27396;color:white;font-size:11px;font-weight:600;display:flex;align-items:center;justify-content:center;z-index:1000;cursor:help">${count}</span>` : '';
      const menuBtn = d.data.id !== 'virtual-root-org' ? `<button class="node-menu-btn" data-node-id="${d.data.id}" data-user-id="${d.data.id}" data-type="user" title="Options">&#8942;</button>` : '';
      return `
        <div class="node-container" style='width:${d.width}px;height:${d.height}px;padding:1px; background-color: var(--v-newDesignBackground-base);position:relative'>
          ${countBadge}
          ${menuBtn}
          <div class="content-container" style="font-family: 'Inter', sans-serif; width:${d.width - 2}px;height:${d.height - 2}px;border-radius:10px;border: ${d.data._assignmentPathHighlighted || d.data._assignmentParentHighlighted ? '4px solid #43A047' : (d.data._highlighted || d.data._upToTheRootHighlighted ? '4px solid #E27396' : '1px solid #167bd7')};overflow:hidden;display:flex;flex-direction:column;padding:10px 12px 10px 12px;box-sizing:border-box;position:relative">
            <div class="v-avatar avatar cursor-pointer grey" style="height:36px;min-width:36px;width:36px;flex-shrink:0;border-radius:50%;overflow:hidden;">
              <img src="${d.data.profilePicSrc || ''}" onerror="this.onerror=null;this.src='https://cdn.vuetifyjs.com/images/parallax/material2.jpg'" style="width:36px;height:36px;object-fit:cover;">
            </div>
            <div style="margin-top:auto;${title ? '' : 'padding-bottom:0'}">
              <div style="font-size:13px;color:var(--v-text-base);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${d.data.blueTag}</div>
              ${title ? `<div style="color:#716E7B;margin-top:2px;font-size:10px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${title}</div>` : ''}
            </div>
          </div>
        </div>
      `;
    },

    generateAssignmentContent(d) {
      const project = d.data.projectName || '';
      const jobTitle = d.data.jobTitle || d.data.roles || '';
      const count = this.getAppearanceCount(d.data);
      const hoverText = (this.getCountHoverText(d.data) || '').replace(/"/g, '&quot;').replace(/\n/g, '&#10;');
      const countBadge = count > 1 ? `<span class="node-appearance-count" data-tooltip="${hoverText}" style="position:absolute;top:8px;right:6px;min-width:20px;height:20px;padding:0 5px;border-radius:12px;background:#43A047;color:white;font-size:11px;font-weight:600;display:flex;align-items:center;justify-content:center;z-index:1000;cursor:help">${count}</span>` : '';
      const highlightedClass = d.data._highlighted ? 'assignment-highlighted' : '';
      const borderStyle = d.data._highlighted ? '4px solid #43A047' : '1.5px solid #167bd7';
      return `
        <div class="node-container" style='width:${d.width}px;height:${d.height}px;padding:1px; background-color: var(--v-newDesignBackground-base);position:relative'>
          ${countBadge}
          <button class="node-menu-btn" data-node-id="${d.data.id}" data-user-id="${d.data.userId}" data-type="assignment" title="Options">&#8942;</button>
          <div class="content-container ${highlightedClass}" style="font-family: 'Inter', sans-serif; width:${d.width - 2}px;height:${d.height - 2}px;border-radius:10px;border:${borderStyle};overflow:hidden;display:flex;flex-direction:column;padding:10px 12px 10px 12px;box-sizing:border-box;position:relative">
            <div class="v-avatar avatar cursor-pointer grey" style="height:36px;min-width:36px;width:36px;flex-shrink:0;border-radius:50%;overflow:hidden;">
              <img src="${d.data.profilePicSrc || ''}" onerror="this.onerror=null;this.src='https://cdn.vuetifyjs.com/images/parallax/material2.jpg'" style="width:36px;height:36px;object-fit:cover;">
            </div>
            <div style="margin-top:auto;">
              <div style="font-size:13px;color:var(--v-text-base);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${d.data.blueTag}</div>
              ${jobTitle ? `<div style="color:#716E7B;margin-top:2px;font-size:10px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${jobTitle}</div>` : ''}
              ${project ? `<div style="color:#43A047;margin-top:2px;font-size:10px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${project}</div>` : ''}
            </div>
          </div>
        </div>
        
      `;
    }
  }
};
</script>

<style lang="scss" scoped>
.user-autocomplete {
  max-width: 300px;
}
.col-login {
  padding-bottom: 0px !important;
  max-height: 120px !important;

  span {
    font-weight: 400;
    color:darkgrey;
    margin-bottom: 8px;
  }
}
.node-container { }
.content-container { background-color: #ffffff; }

.drag-enabled:not(.dragging-active) .node.draggable rect {
  stroke: grey;
  stroke-width: 3px;
  stroke-dasharray: 5px;
  /* stroke: var(--v-primary-base); */
  cursor: grab;
}

.drag-enabled.dragging-active .droppable rect {
  stroke: var(--v-primary-base);
  stroke-width: 3px;
  stroke-dasharray: 5px;
}

.node.dragging rect {
  stroke-dasharray: 0 !important;
  stroke-width: 0 !important;
}

.node.dragging .content-container {
  background-color: #ffffff;
  box-shadow: 0 5px 15px rgba(0,0,0,0.2);
  cursor: grabbing;
}

.node.drop-target .content-container {
  opacity: 0.5;
  background-color: #E0EDFE;
  transition: opacity 0.15s ease, background-color 0.15s ease;
}
</style>

<style>
@keyframes pulse-glow {
  0%   { box-shadow: 0 0 0 0px rgba(205, 99, 95, 0.7); }
  60%  { box-shadow: 0 0 0 15px rgba(67, 160, 71, 0); }
  100% { box-shadow: 0 0 0 0px rgba(67, 160, 71, 0); }
}

.assignment-highlighted {
  animation: pulse-glow 1.4s ease-out infinite;
}
</style>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500&display=swap');

.org-chart-wrapper {
  font-family: 'Inter', sans-serif;
  background-color: var(--v-newDesignBackground-base);
  height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.controls {
  padding: 10px;
  background: var(--v-newDesignBackground-base);;
  border-bottom: 1px solid var(--v-background-lighten2);
  display: flex;
  gap: 10px;
  align-items: center;
  z-index: 10;
}

.search-field {
  min-width: 200px;
  max-width: 240px;
}

.chart-and-sidebar {
  flex-grow: 1;
  display: flex;
  flex-direction: row;
  position: relative;
  overflow: hidden;
}

.chart-container {
  flex-grow: 1;
  min-width: 0;
  position: relative;
  overflow: hidden;
  background-color: var(--v-newDesignBackground-base);
}

.chart-legend {
  position: absolute;
  top: 20px;
  left: 20px;
  background: var(--v-newDesignBackground-base);
  border: 1px solid rgba(0,0,0,0.12);
  border-radius: 8px;
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 10;
  pointer-events: none;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.chart-legend-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  color: var(--v-text-base);
  white-space: nowrap;
}

.loading-container {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--v-newDesignBackground-base);
  z-index: 5;
}

.loading-message {
  font-size: 16px;
  color: #716E7B;
}
::v-deep path.link {
  fill: none;
  stroke-width: 2px;
  stroke-opacity: 0.6;
}
::v-deep path.link.link-highlighted {
  stroke-width: 3px !important;
  stroke-opacity: 1 !important;
}
::v-deep .node-appearance-count[data-tooltip] {
  position: absolute;
}
::v-deep .node-appearance-count[data-tooltip]:hover::after {
  content: attr(data-tooltip);
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  padding: 8px 12px;
  min-width: 240px;
  max-width: 560px;
  background: rgba(0, 0, 0, 0.9);
  color: white;
  font-size: 12px;
  font-family: 'Inter', sans-serif;
  white-space: pre-line;
  text-align: left;
  border-radius: 4px;
  z-index: 100002;
  pointer-events: none;
  margin-bottom: 6px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}
/*
::v-deep .svg-chart-container {
  width: 100%;
}
*/

.action-btn {
  padding: 8px 16px;
  border-radius: 4px;
  border: 1px solid #ccc;
  cursor: pointer;
  background: var(--v-newDesignBackground-base);
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.action-btn.primary {
  background-color: var(--v-primary-base);
  color: white;
  border: none;
}
.action-btn.warning {
  background-color: var(--v-error-base) !important;
  color: white;
  border: none;
}

.drag-actions {
  display: flex;
  gap: 10px;
}

/* ── Node Sidebar ── */
.node-sidebar {
  position: absolute;
  top: 12px;
  right: 12px;
  bottom: 12px;
  width: 270px;
  background: var(--v-newDesignBackground-base);
  border: 1px solid var(--v-background-lighten2);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 16px 20px;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0,0,0,0.14);
  z-index: 20;
}

.sidebar-close {
  position: absolute;
  top: 10px;
  right: 10px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #716E7B;
  padding: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}
.sidebar-close:hover {
  background: var(--v-background-lighten2);
}

.sidebar-avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid #167bd7;
  flex-shrink: 0;
  position: relative;
  margin-top: 8px;
  margin-bottom: 12px;
  cursor: pointer;
}
.sidebar-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.sidebar-avatar-edit-overlay {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.15s;
}
.sidebar-avatar:hover .sidebar-avatar-edit-overlay {
  opacity: 1;
}

.sidebar-user-autocomplete {
  width: 100%;
}

.sidebar-name {
  font-size: 15px;
  font-weight: 600;
  color: #08011E;
  text-align: center;
  line-height: 1.3;
}
.sidebar-bluetag {
  font-size: 12px;
  color: #167bd7;
  margin-top: 3px;
  text-align: center;
}

.sidebar-section {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sidebar-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: #3a3a4a;
  line-height: 1.4;
}
.sidebar-icon {
  color: #716E7B;
  flex-shrink: 0;
  margin-top: 1px;
}
.sidebar-email {
  word-break: break-all;
}

.sidebar-label {
  font-size: 11px;
  font-weight: 600;
  color: #716E7B;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  width: 100%;
  margin-bottom: 2px;
}
.sidebar-chips {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
}

.sidebar-edit-btn {
  width: 100%;
  margin-top: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

/* slide-in animation */
@keyframes sidebar-slide-in {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.node-sidebar {
  animation: sidebar-slide-in 0.22s ease;
}

/* ── Node 3-dot menu button ── */
::v-deep .node-menu-btn {
  position: absolute;
  bottom: 5px;
  right: 6px;
  z-index: 10;
  width: 20px;
  height: 20px;
  padding: 0;
  border: 1px solid rgba(0,0,0,0.18);
  border-radius: 12px;
  background: var(--v-newDesignBackground-base, #fff);
  box-shadow: 0 1px 4px rgba(0,0,0,0.15);
  color: #757575;
  font-size: 16px;
  line-height: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s, box-shadow 0.15s;
}
::v-deep .node-menu-btn:hover {
  background: rgba(0,0,0,0.06);
  color: #212121;
  box-shadow: 0 2px 8px rgba(0,0,0,0.22);
}

/* ── Node action dropdown ── */
.node-menu-backdrop {
  position: fixed;
  inset: 0;
  z-index: 999;
}
.node-action-menu {
  position: fixed;
  z-index: 1000;
  background: var(--v-newDesignBackground-base, #fff);
  border: 1px solid rgba(0,0,0,0.12);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  min-width: 150px;
  padding: 4px 0;
  display: flex;
  flex-direction: column;
}
.node-action-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  color: var(--v-text-base, #333);
  width: 100%;
  text-align: left;
  transition: background 0.12s;
}
.node-action-item:hover {
  background: rgba(0,0,0,0.06);
}
</style>